import React, { useEffect, useState, useRef, useCallback } from "react";
import { Box, FormHelperText, Typography, styled, InputLabel, FormControl } from "@mui/material";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Icon from "@/theme/vectors/bundle";

// Custom toolbar component with your own SVG icons
const CustomToolbar = ({ id }) => (
    <div id={`toolbar-${id}`} className="custom-quill-toolbar">
        {/* Heading buttons */}
        <span className="ql-formats">
            <button className="ql-header" value="2" title="Heading 2">
                <span className="custom-icon">H2</span>
            </button>
            <button className="ql-header" value="3" title="Heading 3">
                <span className="custom-icon">H3</span>
            </button>
            <button className="ql-header" value="4" title="Heading 4">
                <span className="custom-icon">H4</span>
            </button>
            <button className="ql-header" title="Normal">
                <span className="custom-icon">P</span>
            </button>
        </span>
        
        {/* Formatting buttons */}
        <span className="ql-formats">
            <button className="ql-bold" title="Bold">
                <span className="custom-icon">B</span>
            </button>
            <button className="ql-italic" title="Italic">
                <span className="custom-icon">I</span>
            </button>
            <button className="ql-underline" title="Underline">
                <span className="custom-icon">U</span>
            </button>
        </span>
        
        {/* List buttons */}
        <span className="ql-formats">
            <button className="ql-list" value="ordered" title="Numbered List">
                <span className="custom-icon icon-wrapper">
                    <Icon name="orderedList" size={15} />
                </span>
            </button>
            <button className="ql-list" value="bullet" title="Bullet List">
                <span className="custom-icon icon-wrapper">
                    <Icon name="unorderedList" size={15} />
                </span>
            </button>
        </span>
        
        {/* Indentation buttons */}
        <span className="ql-formats">
            <button className="ql-indent" value="-1" title="Decrease Indent">
                <span className="custom-icon icon-wrapper">
                    <Icon name="leftChevron" size={15} />
                </span>
            </button>
            <button className="ql-indent" value="+1" title="Increase Indent">
                <span className="custom-icon icon-wrapper">
                    <Icon name="rightChevron" size={15} />
                </span>
            </button>
        </span>
        
        {/* Link button */}
        <span className="ql-formats">
            <button className="ql-link" title="Insert Link">
                <span className="custom-icon icon-wrapper">
                    <Icon name="link" size={15} />
                </span>
            </button>
        </span>
        
        {/* Image button */}
        <span className="ql-formats">
            <button className="ql-image" title="Insert Image">
                <span className="custom-icon icon-wrapper">
                    <Icon name="image" size={15} />
                </span>
            </button>
        </span>
        
        {/* Video button */}
        <span className="ql-formats">
            <button className="ql-video" title="Insert Video">
                <span className="custom-icon icon-wrapper">
                    <Icon name="halfDottedCirclePlay" size={15} />
                </span>
            </button>
        </span>
        
        {/* Clean formatting button */}
        <span className="ql-formats">
            <button className="ql-clean" title="Clear Formatting">
                <span className="custom-icon icon-wrapper">
                    <Icon name="boltSlash" size={15} />
                </span>
            </button>
        </span>
    </div>
);

// Styled container for the Quill editor
const StyledQuillContainer = styled(Box)(({ theme }) => ({
    backgroundColor: 'white',
    border: '1px solid #ddd',
    borderRadius: '4px',
    position: 'relative',
    '&:hover': {
        borderColor: '#bbb',
    },
    '&:focus-within': {
        borderColor: '#aaa',
    },
    // Default toolbar styling (hidden when using custom toolbar)
    '& .ql-toolbar.ql-snow': {
        display: 'none',
    },
    '& .custom-quill-toolbar': {
        position: 'absolute',
        zIndex: 10,
        background: 'rgba(255, 255, 255, 0.95)',
        borderRadius: '4px',
        border: '1px solid #ddd',
        boxShadow: '0 2px 5px rgba(0,0,0,0.15)',
        padding: '3px 5px', // Reduce vertical padding slightly
        transition: 'opacity 0.2s ease, transform 0.2s ease',
        opacity: 0, // Start hidden
        pointerEvents: 'none', // Ignore mouse events when hidden
    },
    '& .custom-quill-toolbar.show-toolbar': {
        opacity: 1,
        pointerEvents: 'all', // Re-enable mouse events
    },
    '& .ql-container': {
        border: 'none',
        fontSize: '13px',
        minHeight: '200px',
    },
    '& .ql-editor': {
        minHeight: '200px',
        padding: '15px',
    },
    // Add spacing to paragraphs and headings
    '& .ql-editor p': {
        marginBottom: '1em',
    },
    // Limit media elements to max-width of 500px
    '& .ql-editor img, & .ql-editor video, & .ql-editor audio': {
        maxWidth: '500px',
        height: 'auto',
        display: 'block'
    },
    '& .ql-editor h2, & .ql-editor h3, & .ql-editor h4': {
        marginTop: '1.2em',
        marginBottom: '0.8em',
    },
    // Style headings with colors
    '& .ql-editor h2': {
        color: '#e67e22', // orange
        fontSize: '1.5em',
    },
    '& .ql-editor h3': {
        color: '#3498db', // blue
        fontSize: '1.3em',
    },
    '& .ql-editor h4': {
        color: '#2ecc71', // green
        fontSize: '1.1em',
    },
    // Custom toolbar button styles
    '.custom-quill-toolbar .ql-header[value="2"] .custom-icon': {
        color: '#e67e22', // orange
        fontWeight: 'bold',
        fontSize: '13px', // Consistent font size
    },
    '.custom-quill-toolbar .ql-header[value="3"] .custom-icon': {
        color: '#3498db', // blue
        fontWeight: 'bold',
        fontSize: '13px', // Consistent font size
    },
    '.custom-quill-toolbar .ql-header[value="4"] .custom-icon': {
        color: '#2ecc71', // green
        fontWeight: 'bold',
        fontSize: '13px', // Consistent font size
    },
    '.custom-quill-toolbar .ql-header[value="false"] .custom-icon': {
        color: '#666', // dark gray
        fontWeight: 'bold',
        fontSize: '13px', // Consistent font size
    },
    // Format button styles
    '.custom-quill-toolbar .ql-bold .custom-icon': {
        color: '#333',
        fontWeight: 'bold',
        fontSize: '13px', // Consistent font size
    },
    '.custom-quill-toolbar .ql-italic .custom-icon': {
        color: '#333',
        fontWeight: 'bold',
        fontStyle: 'italic',
        fontSize: '13px', // Consistent font size
    },
    '.custom-quill-toolbar .ql-underline .custom-icon': {
        color: '#333',
        fontWeight: 'bold',
        textDecoration: 'underline',
        fontSize: '13px', // Consistent font size
    },
    // General styles for all custom-icon elements
    '.custom-quill-toolbar .custom-icon': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 1,
    },
    // Specific adjustment for text buttons (non-icon buttons)
    '.custom-quill-toolbar .custom-icon:not(.icon-wrapper)': {
        display: 'inline-flex',  // Add inline-flex for better alignment
        alignItems: 'center',    // Center text vertically
        justifyContent: 'center', // Center text horizontally
        height: '100%',          // Take full height of button
        padding: 0,              // Remove padding
        fontSize: '13px',        // Consistent font size
    },
    // For SVG icons - ensures consistent height with text buttons
    '.custom-quill-toolbar .custom-icon.icon-wrapper': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    // Custom button styling
    '.custom-quill-toolbar button': {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '30px',
        height: '28px',
        padding: '0', // Remove padding for more consistent alignment
        borderRadius: '3px',
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        verticalAlign: 'middle', // Ensure consistent vertical alignment
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
        },
        '&.ql-active': {
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
        },
    },
    // Add spacing between button groups
    '.custom-quill-toolbar .ql-formats': {
        marginRight: '8px',
        display: 'inline-flex',
    },
}));

// Styled container for the Quill editor with floating label
const FloatingLabelQuillContainer = styled(FormControl)(({ theme }) => ({
    position: 'relative',
    width: '100%',
    
    '& .MuiInputLabel-root': {
        position: 'absolute',
        top: 0,
        left: 0,
        padding: '0 5px',
        fontSize: '13px',
        color: 'rgba(0, 0, 0, 0.6)',
        transform: 'translate(14px, 16px) scale(1)',
        transformOrigin: 'top left',
        zIndex: 1,
        transition: 'color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms, transform 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms, top 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',
        backgroundColor: 'white',
        pointerEvents: 'none',
        
        '&.MuiInputLabel-shrink': {
            transform: 'translate(14px, -6px) scale(0.75)',
        },
        
        '&.Mui-focused': {
            color: 'rgba(0, 0, 0, 0.87)',
        },
        
        '&.Mui-error': {
            color: theme.palette.error.main,
        },
    },
    
    '&:hover .MuiInputBase-root:not(.Mui-disabled):before': {
        borderColor: 'rgba(0, 0, 0, 0.87)',
    },
}));

// Quill modules configuration
const defaultModules = {
    toolbar: [
        [{ 'header': 2 }, { 'header': 3 }, { 'header': 4 }, { 'header': false }],
        ['bold', 'italic', 'underline'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'indent': '-1' }, { 'indent': '+1' }],
        ['link', 'image', 'video'],
        ['clean'],
    ],
};

// Quill formats configuration
const defaultFormats = [
    'header',
    'bold', 'italic', 'underline',
    'list', 'bullet', 'indent',
    'link', 'image', 'video',
];

export const HtmlField = ({
    label,
    value,
    onChange,
    helperText,
    disabled = false,
    placeholder = "",
    error = false,
    minHeight = 200,
    useCustomIcons = true, // New prop to toggle custom icons
    floatingLabel = true, // New prop to toggle floating label
    ...props
}) => {
    const [editorValue, setEditorValue] = useState(value || "");
    const [isFocused, setIsFocused] = useState(false);
    const editorRef = useRef(null);
    const containerRef = useRef(null);
    const toolbarRef = useRef(null);
    const quillRef = useRef(null);
    const [toolbarId] = useState(`toolbar-${Math.random().toString(36).substr(2, 9)}`);

    // Define custom modules with toolbar container ID
    const modules = useCustomIcons ? {
        toolbar: {
            container: `#${toolbarId}`,
        }
    } : defaultModules;

    // Style adjustments for the Link icon
    const linkIconStyle = {
        width: '18px',
        height: '18px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    };

    // Quill formats configuration
    const formats = [
        'header',
        'bold', 'italic', 'underline',
        'list', 'bullet', 'indent',
        'link', 'image', 'video',
    ];

    // Update internal state when external value changes
    useEffect(() => {
        setEditorValue(value || "");
    }, [value]);

    const handleChange = (content) => {
        setEditorValue(content);
        if (onChange) {
            // Create a synthetic event-like object for consistency with other input fields
            onChange({
                target: {
                    name: props.name,
                    value: content
                }
            });
        }
    };

    // Position the toolbar based on selection
    const updateToolbarPosition = useCallback(() => {
        if (!quillRef.current || !toolbarRef.current || !containerRef.current) return;

        const quill = quillRef.current.getEditor();
        const selection = quill.getSelection();
        
        if (!selection || selection.length === 0) {
            // Hide toolbar when no selection
            toolbarRef.current.classList.remove('show-toolbar');
            return;
        }

        // Show toolbar
        toolbarRef.current.classList.add('show-toolbar');
        
        // Get container dimensions and position
        const containerRect = containerRef.current.getBoundingClientRect();
        const toolbarRect = toolbarRef.current.getBoundingClientRect();
        
        // Get position of text cursor for the start of the selection
        const rangeBounds = quill.getBounds(selection.index, selection.length);
        
        // Calculate initial position
        let left = rangeBounds.left + rangeBounds.width / 2 - toolbarRect.width / 2;
        let top = rangeBounds.top - toolbarRect.height - 10; // 10px above selection

        // Adjust if toolbar would go outside container bounds
        // Check if too far left
        if (left < 0) {
            left = 5; // 5px padding from left edge
        }
        
        // Check if too far right
        if (left + toolbarRect.width > containerRect.width) {
            left = containerRect.width - toolbarRect.width - 5; // 5px from right edge
        }
        
        // If too high, place below the selection instead
        if (top < 0) {
            top = rangeBounds.top + rangeBounds.height + 10; // 10px below selection
        }

        // Apply position
        toolbarRef.current.style.left = `${left}px`;
        toolbarRef.current.style.top = `${top}px`;
    }, []);

    // Handle focus and blur for floating label
    const handleEditorFocus = () => {
        setIsFocused(true);
    };

    const handleEditorBlur = () => {
        setIsFocused(false);
    };
    
    // Check if editor is empty
    const isEditorEmpty = editorValue === "" || editorValue === "<p><br></p>";

    // Initialize editor references and event listeners
    useEffect(() => {
        if (!quillRef.current || disabled) return;

        const quill = quillRef.current.getEditor();
        
        // Find the toolbar element after the component is mounted
        const toolbar = containerRef.current.querySelector('.custom-quill-toolbar');
        if (toolbar) {
            toolbarRef.current = toolbar;
        }

        // Set up selection change handler
        const handleSelectionChange = (range) => {
            if (range) {
                updateToolbarPosition();
                handleEditorFocus();
            } else {
                // Hide toolbar when editor loses focus
                if (toolbarRef.current) {
                    toolbarRef.current.classList.remove('show-toolbar');
                }
                handleEditorBlur();
            }
        };

        quill.on('selection-change', handleSelectionChange);

        // Clean up event listener on unmount
        return () => {
            quill.off('selection-change', handleSelectionChange);
        };
    }, [updateToolbarPosition, disabled]);

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            updateToolbarPosition();
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [updateToolbarPosition]);

    // Content for the regular label version
    const regularLabelContent = (
        <Box>
            {label && (
                <Typography 
                    variant="subtitle1" 
                    sx={{ 
                        mb: 1, 
                        fontSize: '13px',
                        color: error ? 'error.main' : 'text.primary'
                    }}
                >
                    {label}
                </Typography>
            )}
            {isEditorEmpty && !isFocused && placeholder && (
                <Typography
                    sx={{
                        position: 'absolute',
                        top: '15px',
                        left: '15px',
                        color: 'text.secondary',
                        pointerEvents: 'none',
                        zIndex: 1,
                        fontSize: '13px'
                    }}
                >
                    {placeholder}
                </Typography>
            )}
            <StyledQuillContainer
                ref={containerRef}
                sx={{
                    border: error ? '1px solid' : '1px solid #ddd',
                    borderColor: error ? 'error.main' : '#ddd',
                    '& .ql-editor': {
                        minHeight: `${minHeight}px`,
                    },
                }}
            >
                {useCustomIcons && <CustomToolbar id={toolbarId.replace('toolbar-', '')} />}
                <ReactQuill
                    ref={quillRef}
                    theme={useCustomIcons ? null : "snow"}
                    value={editorValue}
                    onChange={handleChange}
                    modules={modules}
                    formats={formats}
                    readOnly={disabled}
                    placeholder={placeholder}
                    {...props}
                />
            </StyledQuillContainer>
            {helperText && (
                <FormHelperText error={error}>{helperText}</FormHelperText>
            )}
        </Box>
    );

    // Content for the floating label version
    const floatingLabelContent = (
        <FloatingLabelQuillContainer error={error} fullWidth>
            {isEditorEmpty && !isFocused && placeholder && (
                <Typography
                    sx={{
                        position: 'absolute',
                        top: '35px',
                        left: '15px',
                        color: 'text.secondary',
                        pointerEvents: 'none',
                        zIndex: 1,
                        fontSize: '13px'
                    }}
                >
                    {placeholder}
                </Typography>
            )}
            <InputLabel 
                shrink={isFocused || !isEditorEmpty}
                error={error}
                htmlFor={`quill-editor-${toolbarId}`}
            >
                {label}
            </InputLabel>
            <StyledQuillContainer
                ref={containerRef}
                sx={{
                    border: error ? '1px solid' : '1px solid #ddd',
                    borderColor: error ? 'error.main' : '#ddd',
                    '& .ql-editor': {
                        minHeight: `${minHeight}px`,
                        paddingTop: '20px', // Extra padding to accommodate floating label
                    },
                }}
            >
                {useCustomIcons && <CustomToolbar id={toolbarId.replace('toolbar-', '')} />}
                <ReactQuill
                    ref={quillRef}
                    theme={useCustomIcons ? null : "snow"}
                    value={editorValue}
                    onChange={handleChange}
                    modules={modules}
                    formats={formats}
                    readOnly={disabled}
                    placeholder={placeholder}
                    id={`quill-editor-${toolbarId}`}
                    {...props}
                />
            </StyledQuillContainer>
            {helperText && (
                <FormHelperText error={error}>{helperText}</FormHelperText>
            )}
        </FloatingLabelQuillContainer>
    );

    return floatingLabel ? floatingLabelContent : regularLabelContent;
};

// Export a simple version with fewer toolbar options and custom icons
export const SimpleHtmlField = (props) => {
    return (
        <HtmlField
            useCustomIcons={true}
            minHeight={150}
            floatingLabel={true}
            {...props}
        />
    );
}; 